import {
  DEPLOYMENT_ENVIRONMENTS,
  DEPLOYMENT_MODES,
  DEPLOYMENT_PIPELINES,
  DEPLOYMENT_STATS_DAY_OPTIONS,
  type DeploymentPipelineAction,
} from '@dev-console/deployments';
import type {
  DeploymentEnvironment,
  DeploymentMode,
  DeploymentPipeline,
  DeploymentPipelineStatus,
  DeploymentPipelineTarget,
  DeploymentRecord,
  DeploymentStatsBucket,
  DeploymentStatsDays,
  DeploymentStatsResponse,
} from '@dev-console/types';
import { Pod } from '@shared/pods/constants';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import type { TooltipProps } from 'recharts';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DeploymentDrawer } from '@/components/deployments/deployment-drawer';
import {
  canRollbackDeployment,
  formatDuration,
  relativeTime,
  totalDurationSeconds,
} from '@/components/deployments/deployment-format';
import { DeploymentPullRequests } from '@/components/deployments/deployment-pull-requests';
import { DeploymentTimeline } from '@/components/deployments/deployment-timeline';
import { DisablePipelinesDialog } from '@/components/deployments/disable-pipelines-dialog';
import { MergeFreezeDialog } from '@/components/deployments/merge-freeze-dialog';
import { RollbackDialog } from '@/components/deployments/rollback-dialog';
import { ApiError } from '@/config/api';
import {
  cancelDeployment,
  getCurrentDeployments,
  getDeploymentHistory,
  getDeploymentPipelineStatus,
  getDeploymentStats,
  getMergeFreezeStatus,
} from '@/config/api-helpers';
import { useFetch } from '@/hooks/useFetch';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { useAuth } from '@/lib/auth-context';

const INCIDENT_LEAD_GROUP = 'SG-Eng Incident Lead';

function canMutateDeployments(user: { pods?: string[]; groups?: string[] } | null): boolean {
  if (user == null) return false;
  return (user.pods ?? []).includes(Pod.PLATFORM) || (user.groups ?? []).includes(INCIDENT_LEAD_GROUP);
}

/** Consumer pod owns the mobile app, so it can also pause OTAs. Mirrors the server check. */
function canToggleOta(user: { pods?: string[]; groups?: string[] } | null): boolean {
  return canMutateDeployments(user) || (user?.pods ?? []).includes(Pod.CONSUMER);
}

export const meta = {
  label: 'Deployments',
  order: 3,
};

const STATUS_DOT_CLASS: Record<string, string> = {
  success: 'bg-green-500',
  failure: 'bg-red-500',
  cancelled: 'bg-neutral-400',
  skipped: 'bg-neutral-300',
  in_progress: 'bg-indigo-500 animate-pulse',
};

const STATUS_DOT_LABEL: Record<string, string> = {
  success: 'success',
  failure: 'failed',
  cancelled: 'cancelled',
  skipped: 'skipped',
  in_progress: 'in progress',
};

type DeploymentsView = 'pipeline' | 'stats';

const PIPELINE_TABS = DEPLOYMENT_ENVIRONMENTS.map((environment) => ({
  id: environment,
  label: environment === 'staging' ? 'Staging' : 'Production',
}));

const GITHUB_COMMIT_URL = 'https://github.com/long-view-labs/mono-repo/commit';
const GITHUB_COMPARE_URL = 'https://github.com/long-view-labs/mono-repo/compare';
const COLOR_DEPLOYS = '#285276';
const COLOR_FAILURES = '#9B4728';
const COLOR_DURATION = '#6B5B95';
const COLOR_COMMITS = '#417428';

/** Shared size for Workflow / Cancel / Rollback — full-width on mobile, fixed min width from sm up. */
const ACTION_BUTTON_CLASS =
  'inline-flex h-8 w-full shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2.5 text-xs font-medium sm:w-auto sm:min-w-[8.5rem]';
const ACTION_BUTTON_SECONDARY_CLASS = `${ACTION_BUTTON_CLASS} border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50`;
const ACTION_BUTTON_DANGER_OUTLINE_CLASS = `${ACTION_BUTTON_CLASS} border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50`;
const ACTION_BUTTON_PRIMARY_CLASS = `${ACTION_BUTTON_CLASS} bg-neutral-900 text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50`;
const SECTION_HEADING_CLASS = 'text-base font-semibold text-neutral-800';
const IN_FLIGHT_CARD_CLASS =
  'min-w-0 overflow-hidden rounded-lg border border-blue-200 bg-white p-4 shadow-sm shadow-blue-100/40';
const ACTION_CLUSTER_CLASS =
  'flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end';
const MODE_SELECT_CLASS = 'min-w-0 max-w-full rounded-md border border-neutral-200 bg-white px-2 py-1 text-sm';

type PageErrorBanner = {
  title: string;
  message: string;
  status?: number;
};

const SNAKE_CASE_CODE = /^[a-z]+(?:_[a-z]+)+$/;
type PageErrorSource = 'fetch' | 'action';

function detailFromUnknown(err: unknown): { detail: string; status?: number } {
  if (err instanceof ApiError) {
    const detail =
      'message' in err.body && typeof err.body.message === 'string' && err.body.message.trim()
        ? err.body.message
        : err.message.replace(/_/g, ' ');
    return { detail, status: err.status };
  }
  if (err instanceof Error) {
    const detail = SNAKE_CASE_CODE.test(err.message) ? err.message.replace(/_/g, ' ') : err.message;
    return { detail };
  }
  return { detail: String(err) };
}

function pageErrorFromUnknown(err: unknown, title: string): PageErrorBanner {
  const { detail, status } = detailFromUnknown(err);
  const normalized = detail.trim().toLowerCase();
  const looksLikeCode =
    !detail.trim() ||
    SNAKE_CASE_CODE.test(detail.trim()) ||
    normalized === 'internal server error' ||
    normalized === 'internal_server_error';
  return {
    title,
    message: looksLikeCode ? 'Something went wrong. Try again or check GitHub credentials.' : detail,
    status,
  };
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-neutral-200 ${className}`} aria-hidden='true' />;
}

function deploymentKey(item: Pick<DeploymentRecord, 'workflowRunId' | 'mode'>): string {
  return `${item.workflowRunId}-${item.mode}`;
}

function CommitLink({ sha, short, muted = false }: { sha: string; short?: string; muted?: boolean }) {
  const href = `${GITHUB_COMMIT_URL}/${sha}`;
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={`font-mono hover:underline ${muted ? 'text-neutral-500' : 'text-blue-700'}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        window.open(href, '_blank', 'noopener,noreferrer');
      }}
    >
      {short ?? sha.slice(0, 7)}
    </a>
  );
}

function shaMatches(a: string, b: string): boolean {
  return a === b || a.startsWith(b) || b.startsWith(a);
}

function ChevronToggle({ expanded, onToggle, label }: { expanded: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      type='button'
      className='inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
      aria-expanded={expanded}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <svg
        aria-hidden='true'
        viewBox='0 0 16 16'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        className={`size-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
      >
        <path d='M6 4l4 4-4 4' />
      </svg>
      {label}
    </button>
  );
}

function statusDotLabel(status: string, superseded = false): { word: string; ariaLabel: string } {
  if (status === 'skipped' && superseded) {
    return { word: 'superseded', ariaLabel: 'Skipped because a newer deploy superseded this one' };
  }
  const word = STATUS_DOT_LABEL[status] ?? status.replace(/_/g, ' ');
  return { word, ariaLabel: word };
}

function StatusDot({ status, superseded = false }: { status: string; superseded?: boolean }) {
  const { word, ariaLabel } = statusDotLabel(status, superseded);
  return (
    <span className='mt-1.5 inline-flex shrink-0 items-center gap-1.5' aria-label={ariaLabel}>
      <span
        className={`size-2.5 shrink-0 rounded-full ${STATUS_DOT_CLASS[status] ?? 'bg-neutral-300'}`}
        aria-hidden='true'
      />
      <span className='text-[11px] font-medium leading-none text-neutral-500'>{word}</span>
    </span>
  );
}

function BundledCommitLine({ item }: { item: DeploymentRecord }) {
  const pullRequest = item.pullRequests[0];
  return (
    <li className='min-w-0 text-[11px] leading-tight text-neutral-500'>
      <CommitLink sha={item.commitSha} short={item.commitShaShort} muted />
      {pullRequest != null && (
        <>
          <span className='mx-1 text-neutral-300' aria-hidden='true'>
            |
          </span>
          <a
            href={pullRequest.url}
            target='_blank'
            rel='noopener noreferrer'
            className='min-w-0 truncate text-blue-700 hover:underline'
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.open(pullRequest.url, '_blank', 'noopener,noreferrer');
            }}
          >
            #{pullRequest.number} {pullRequest.title}
          </a>
        </>
      )}
    </li>
  );
}

function BundledCommits({
  count,
  head,
  skipped,
  expanded,
  githubHref,
  onToggle,
}: {
  count: number;
  head: DeploymentRecord;
  skipped: DeploymentRecord[];
  expanded: boolean;
  githubHref: string;
  onToggle: () => void;
}) {
  const listed = skipped.length > 0 ? [head, ...skipped] : [];
  return (
    <div className='min-w-0 space-y-1.5'>
      <div className='flex min-w-0 items-center gap-1'>
        <ChevronToggle expanded={expanded} onToggle={onToggle} label={`${count} commits`} />
        <a
          href={githubHref}
          target='_blank'
          rel='noopener noreferrer'
          className='inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-1 text-[11px] font-medium leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
          aria-label='View commits on GitHub'
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            window.open(githubHref, '_blank', 'noopener,noreferrer');
          }}
        >
          on GitHub
          <ExternalLinkIcon />
        </a>
      </div>
      {expanded && (
        <ul className='min-w-0 space-y-1 border-l border-neutral-200 pl-3'>
          {listed.length === 0 ? (
            <li className='text-[11px] leading-tight text-neutral-400'>
              Bundled commit details not in the current history page
            </li>
          ) : (
            listed.map((item) => <BundledCommitLine key={deploymentKey(item)} item={item} />)
          )}
        </ul>
      )}
    </div>
  );
}

function bundledCommitCount(item: DeploymentRecord, skipped: DeploymentRecord[]): number | null {
  if (skipped.length > 0) return skipped.length + 1;
  if (item.commitCount != null && item.commitCount > 1) return item.commitCount;
  return null;
}

function findBundledSkipped(item: DeploymentRecord, historyItems: DeploymentRecord[]): DeploymentRecord[] {
  return historyItems
    .filter(
      (candidate) =>
        candidate.status === 'skipped' &&
        candidate.environment === item.environment &&
        candidate.mode === item.mode &&
        candidate.supersededBySha != null &&
        shaMatches(candidate.supersededBySha, item.commitSha)
    )
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

function isBundledIntoLaterSuccess(item: DeploymentRecord, historyItems: DeploymentRecord[]): boolean {
  if (item.status !== 'skipped' || item.supersededBySha == null) return false;
  return historyItems.some(
    (candidate) =>
      candidate.status === 'success' &&
      candidate.environment === item.environment &&
      candidate.mode === item.mode &&
      shaMatches(candidate.commitSha, item.supersededBySha ?? '')
  );
}

function findPreviousSuccessfulSha(item: DeploymentRecord, historyItems: DeploymentRecord[]): string | null {
  let previousSha: string | null = null;
  let previousStartedAt: string | null = null;
  for (const candidate of historyItems) {
    if (candidate.status !== 'success') continue;
    if (candidate.environment !== item.environment || candidate.mode !== item.mode) continue;
    if (candidate.workflowRunId === item.workflowRunId) continue;
    if (candidate.startedAt >= item.startedAt) continue;
    if (shaMatches(candidate.commitSha, item.commitSha)) continue;
    if (previousStartedAt == null || candidate.startedAt > previousStartedAt) {
      previousSha = candidate.commitSha;
      previousStartedAt = candidate.startedAt;
    }
  }
  return previousSha;
}

function bundledCommitsGithubHref(
  item: DeploymentRecord,
  skipped: DeploymentRecord[],
  historyItems: DeploymentRecord[]
): string {
  const newest = item.commitSha;
  const previousSuccess = findPreviousSuccessfulSha(item, historyItems);
  if (previousSuccess != null) {
    return `${GITHUB_COMPARE_URL}/${previousSuccess}...${newest}`;
  }
  const oldestSkipped = skipped.length > 0 ? skipped[skipped.length - 1]?.commitSha : null;
  if (oldestSkipped != null && !shaMatches(oldestSkipped, newest)) {
    return `${GITHUB_COMPARE_URL}/${oldestSkipped}...${newest}`;
  }
  return `${GITHUB_COMMIT_URL}/${newest}`;
}

const paramDefs = {
  view: {
    default: 'pipeline' as DeploymentsView,
    parse: (raw: string): DeploymentsView => (raw === 'stats' ? 'stats' : 'pipeline'),
  },
  environment: {
    default: 'staging' as DeploymentEnvironment,
    parse: (raw: string): DeploymentEnvironment =>
      (DEPLOYMENT_ENVIRONMENTS as readonly string[]).includes(raw) ? (raw as DeploymentEnvironment) : 'staging',
  },
  statsEnvironment: {
    default: 'all' as DeploymentEnvironment | 'all',
    parse: (raw: string): DeploymentEnvironment | 'all' => {
      if (raw === 'all') return 'all';
      return (DEPLOYMENT_ENVIRONMENTS as readonly string[]).includes(raw) ? (raw as DeploymentEnvironment) : 'all';
    },
  },
  mode: {
    default: 'all' as DeploymentMode | 'all',
    parse: (raw: string): DeploymentMode | 'all' =>
      (DEPLOYMENT_MODES as readonly string[]).includes(raw) ? (raw as DeploymentMode) : 'all',
  },
  days: {
    default: 30 as DeploymentStatsDays,
    parse: (raw: string): DeploymentStatsDays => {
      const parsed = Number(raw);
      return (DEPLOYMENT_STATS_DAY_OPTIONS as readonly number[]).includes(parsed)
        ? (parsed as DeploymentStatsDays)
        : 30;
    },
  },
  run: {
    default: '',
    parse: (raw: string): string => raw.trim(),
  },
};

function relativeDurationRatio(duration: number | null, maxDuration: number): number {
  if (duration == null || maxDuration <= 0) return 0;
  return Math.min(1, Math.max(0, duration / maxDuration));
}

function DurationMeter({
  durationSeconds,
  maxDurationSeconds,
  buildDurationSeconds,
  deployDurationSeconds,
  muted = false,
}: {
  durationSeconds: number | null;
  maxDurationSeconds: number;
  buildDurationSeconds?: number | null;
  deployDurationSeconds?: number | null;
  muted?: boolean;
}) {
  const ratio = relativeDurationRatio(durationSeconds, maxDurationSeconds);
  const barWidthPct = durationSeconds == null ? 0 : Math.max(ratio * 100, durationSeconds > 0 ? 4 : 0);

  return (
    <div className={`w-full min-w-28 sm:w-36 sm:shrink-0 ${muted ? 'opacity-60' : ''}`}>
      <p className='text-right text-sm font-semibold tabular-nums text-neutral-900'>
        {formatDuration(durationSeconds)}
      </p>
      <div className='mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100' aria-hidden='true'>
        <div
          className='h-full rounded-full bg-neutral-400/80 transition-[width]'
          style={{ width: `${barWidthPct}%` }}
        />
      </div>
      {(buildDurationSeconds != null || deployDurationSeconds != null) && (
        <p className='mt-1 text-right text-[11px] leading-tight text-neutral-500'>
          {[
            buildDurationSeconds != null ? `build ${formatDuration(buildDurationSeconds)}` : null,
            deployDurationSeconds != null ? `deploy ${formatDuration(deployDurationSeconds)}` : null,
          ]
            .filter((part): part is string => part != null)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

function formatChartDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fillStatsBuckets(buckets: DeploymentStatsBucket[], days: DeploymentStatsDays): DeploymentStatsBucket[] {
  const byDay = new Map(buckets.map((bucket) => [bucket.day, bucket]));
  const filled: DeploymentStatsBucket[] = [];
  const end = new Date();
  end.setUTCHours(0, 0, 0, 0);

  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const dayDate = new Date(end);
    dayDate.setUTCDate(end.getUTCDate() - offset);
    const day = dayDate.toISOString().slice(0, 10);
    filled.push(
      byDay.get(day) ?? {
        day,
        total: 0,
        success: 0,
        failure: 0,
        commitCount: 0,
        avgDurationSeconds: null,
      }
    );
  }

  return filled;
}

function DeploymentsTabRow({
  view,
  environment,
  onSelectPipeline,
  onSelectStats,
}: {
  view: DeploymentsView;
  environment: DeploymentEnvironment;
  onSelectPipeline: (environment: DeploymentEnvironment) => void;
  onSelectStats: () => void;
}) {
  const tabClass = (active: boolean) =>
    `shrink-0 px-3 py-2 text-sm font-medium transition-colors sm:px-4 sm:py-3 ${
      active ? 'border-b-2 border-neutral-900 text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'
    }`;

  return (
    <div className='mb-2 flex min-w-0 max-w-full flex-wrap items-end justify-between gap-x-4 border-b border-neutral-200'>
      <div className='flex min-w-0 flex-wrap'>
        {PIPELINE_TABS.map((tab) => (
          <button
            key={tab.id}
            type='button'
            className={tabClass(view === 'pipeline' && environment === tab.id)}
            onClick={() => onSelectPipeline(tab.id as DeploymentEnvironment)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className='flex shrink-0'>
        <button type='button' className={tabClass(view === 'stats')} onClick={onSelectStats}>
          Stats
        </button>
      </div>
    </div>
  );
}

function ThroughputTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || payload == null || payload.length === 0 || label == null) return null;
  const point = payload[0]?.payload as DeploymentStatsBucket | undefined;
  if (point == null) return null;

  return (
    <div className='rounded-md border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm'>
      <p className='mb-1 font-medium text-neutral-900'>{formatChartDay(String(label))}</p>
      <p className='text-neutral-600'>Deploys: {point.total}</p>
      <p className='text-neutral-600'>Commits: {point.commitCount}</p>
      <p className='text-neutral-600'>Success: {point.success}</p>
      <p className='text-neutral-600'>Failure: {point.failure}</p>
      <p className='text-neutral-600'>Avg duration: {formatDuration(point.avgDurationSeconds)}</p>
    </div>
  );
}

function ThroughputChart({ stats }: { stats: DeploymentStatsResponse }) {
  const chartData = useMemo(() => fillStatsBuckets(stats.buckets, stats.days), [stats.buckets, stats.days]);
  const hasCommits = chartData.some((bucket) => bucket.commitCount > 0);
  const hasDuration = chartData.some((bucket) => bucket.avgDurationSeconds != null);

  return (
    <div className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white p-4'>
      <div className='mb-4'>
        <h3 className='text-sm font-medium text-neutral-900'>
          {hasDuration ? 'Volume & duration' : hasCommits ? 'Commit & deploy throughput' : 'Deploy throughput'}
        </h3>
        <p className='mt-1 text-xs text-neutral-500'>
          {hasDuration
            ? `Daily deploy volume and average duration over the last ${stats.days} days`
            : `Daily volume over the last ${stats.days} days`}
        </p>
      </div>
      <div className='h-72 w-full min-w-0'>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke='#E5E5E5' strokeDasharray='3 3' vertical={false} />
            <XAxis
              dataKey='day'
              tickFormatter={formatChartDay}
              tick={{ fill: '#737373', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#E5E5E5' }}
              minTickGap={24}
            />
            <YAxis
              yAxisId='volume'
              allowDecimals={false}
              tick={{ fill: '#737373', fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={32}
            />
            {hasDuration && (
              <YAxis
                yAxisId='duration'
                orientation='right'
                tickFormatter={(value) => formatDuration(typeof value === 'number' ? value : null)}
                tick={{ fill: '#737373', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={44}
              />
            )}
            {!hasDuration && hasCommits && (
              <YAxis
                yAxisId='commits'
                orientation='right'
                allowDecimals={false}
                tick={{ fill: '#737373', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
            )}
            <Tooltip content={<ThroughputTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              formatter={(value) => <span className='text-neutral-600'>{value}</span>}
            />
            <Bar
              yAxisId='volume'
              dataKey='total'
              name='Deploys'
              fill={COLOR_DEPLOYS}
              fillOpacity={0.72}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            <Bar
              yAxisId='volume'
              dataKey='failure'
              name='Failures'
              fill={COLOR_FAILURES}
              fillOpacity={0.55}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
            />
            {hasDuration ? (
              <Line
                yAxisId='duration'
                type='monotone'
                dataKey='avgDurationSeconds'
                name='Avg duration'
                stroke={COLOR_DURATION}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ) : (
              hasCommits && (
                <Line
                  yAxisId='commits'
                  type='monotone'
                  dataKey='commitCount'
                  name='Commits'
                  stroke={COLOR_COMMITS}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HistoryRow({
  item,
  maxDurationSeconds,
  historyItems,
  expandedBundled,
  cancelling,
  canMutate,
  selected,
  onToggleBundled,
  onCancel,
  onRollback,
  onSelect,
}: {
  item: DeploymentRecord;
  maxDurationSeconds: number;
  historyItems: DeploymentRecord[];
  expandedBundled: boolean;
  cancelling: boolean;
  canMutate: boolean;
  selected: boolean;
  onToggleBundled: () => void;
  onCancel: () => void;
  onRollback: () => void;
  onSelect: () => void;
}) {
  const bundledSkipped = item.status === 'success' ? findBundledSkipped(item, historyItems) : [];
  const commitBundleCount = item.status === 'success' ? bundledCommitCount(item, bundledSkipped) : null;
  const showBundled = commitBundleCount != null;
  const duration = totalDurationSeconds(item);
  const skipped = item.status === 'skipped';

  return (
    <li
      className={`min-w-0 px-4 py-3.5 ${skipped ? 'bg-neutral-50/60' : 'bg-white'} ${selected ? 'bg-blue-50/70' : ''}`}
    >
      <div className='flex min-w-0 gap-3'>
        <StatusDot status={item.status} superseded={item.supersededBySha != null} />
        <div className='min-w-0 flex-1 space-y-2.5'>
          <div className='flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
            <div
              className='min-w-0 cursor-pointer space-y-1'
              onClick={onSelect}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect();
                }
              }}
              aria-label={`Open ${item.environment} ${item.mode} deploy ${item.commitShaShort}`}
              role='button'
              tabIndex={0}
            >
              {item.pullRequests.length > 0 ? (
                <DeploymentPullRequests pullRequests={item.pullRequests} compact deploymentCommitSha={item.commitSha} />
              ) : (
                <div className='min-w-0 break-all text-sm font-medium'>
                  <CommitLink sha={item.commitSha} short={item.commitShaShort} muted={skipped} />
                </div>
              )}
              <p className='text-xs text-neutral-500'>
                {item.mode} · {relativeTime(item.startedAt)}
                {item.pullRequests.length === 0 ? '' : ` · ${item.commitShaShort}`}
              </p>
              {skipped && item.supersededBySha != null && (
                <p className='text-[11px] leading-tight text-neutral-500'>
                  as part of <CommitLink sha={item.supersededBySha} muted />
                </p>
              )}
              {skipped && item.supersededBySha == null && (
                <p className='text-[11px] leading-tight text-neutral-400'>superseded by a newer deploy</p>
              )}
              {showBundled && (
                <BundledCommits
                  count={commitBundleCount}
                  head={item}
                  skipped={bundledSkipped}
                  expanded={expandedBundled}
                  githubHref={bundledCommitsGithubHref(item, bundledSkipped, historyItems)}
                  onToggle={onToggleBundled}
                />
              )}
            </div>
            <DurationMeter
              durationSeconds={duration}
              maxDurationSeconds={maxDurationSeconds}
              buildDurationSeconds={item.buildDurationSeconds}
              deployDurationSeconds={item.deployDurationSeconds}
              muted={skipped}
            />
          </div>
        </div>
      </div>
      <div className='mt-2.5 pl-6'>
        <DeploymentActions
          item={item}
          cancelling={cancelling}
          canMutate={canMutate}
          onCancel={onCancel}
          onRollback={onRollback}
        />
      </div>
    </li>
  );
}

export const DeploymentsPage = () => {
  const { user } = useAuth();
  const canMutate = canMutateDeployments(user);
  const {
    view: [view, setView],
    environment: [environment, setEnvironment],
    statsEnvironment: [statsEnvironment, setStatsEnvironment],
    mode: [mode, setMode],
    days: [days, setDays],
    run: [selectedRunId, setSelectedRunId],
  } = useSearchParamsState(paramDefs);

  const [rollbackTarget, setRollbackTarget] = useState<{
    environment: DeploymentEnvironment;
    commitSha: string;
  } | null>(null);
  const [pipelinesDialog, setPipelinesDialog] = useState<{
    pipeline: DeploymentPipelineTarget;
    action: DeploymentPipelineAction;
  } | null>(null);
  const [mergeFreezeDialog, setMergeFreezeDialog] = useState<{ enabled: boolean } | null>(null);
  /** Keys whose bundled-commit lists are expanded (default collapsed). */
  const [expandedBundledKeys, setExpandedBundledKeys] = useState<Set<string>>(() => new Set());
  const [cancellingRunIds, setCancellingRunIds] = useState<Set<string>>(() => new Set());
  const [pageError, setPageError] = useState<PageErrorBanner | null>(null);
  const [pageErrorSource, setPageErrorSource] = useState<PageErrorSource | null>(null);
  const [actionWarning, setActionWarning] = useState<string | null>(null);

  const currentUrl = '/dev-console/deployments/current';
  const historySp = new URLSearchParams({ limit: '50', environment });
  if (mode !== 'all') historySp.set('mode', mode);
  const historyUrl = `/dev-console/deployments?${historySp}`;

  const statsSp = new URLSearchParams({ days: String(days) });
  if (statsEnvironment !== 'all') statsSp.set('environment', statsEnvironment);
  if (mode !== 'all') statsSp.set('mode', mode);
  const statsUrl = `/dev-console/deployments/stats?${statsSp}`;

  const current = useFetch<Awaited<ReturnType<typeof getCurrentDeployments>>>(currentUrl);
  const history = useFetch<Awaited<ReturnType<typeof getDeploymentHistory>>>(view === 'pipeline' ? historyUrl : null);
  const stats = useFetch<Awaited<ReturnType<typeof getDeploymentStats>>>(view === 'stats' ? statsUrl : null);
  const pipelines = useFetch<Awaited<ReturnType<typeof getDeploymentPipelineStatus>>>(
    view === 'pipeline' ? '/dev-console/deployments/pipelines' : null
  );
  const mergeFreeze = useFetch<Awaited<ReturnType<typeof getMergeFreezeStatus>>>(
    view === 'pipeline' ? '/dev-console/deployments/merge-freeze' : null
  );
  const refetchMergeFreeze = mergeFreeze.refetch;

  useEffect(() => {
    if (view !== 'pipeline') return;
    const interval = window.setInterval(() => void refetchMergeFreeze(), 15_000);
    return () => window.clearInterval(interval);
  }, [refetchMergeFreeze, view]);

  const refresh = () => {
    void current.refetch();
    void history.refetch();
    void stats.refetch();
    void pipelines.refetch();
    void mergeFreeze.refetch();
  };

  const showPageError = (err: unknown, title: string) => {
    setPageError(pageErrorFromUnknown(err, title));
    setPageErrorSource('action');
  };

  useEffect(() => {
    if (current.errorCause != null) {
      setPageError(pageErrorFromUnknown(current.errorCause, "Couldn't load currently deployed"));
      setPageErrorSource('fetch');
      return;
    }
    if (view === 'pipeline' && history.errorCause != null) {
      setPageError(pageErrorFromUnknown(history.errorCause, "Couldn't load deployment history"));
      setPageErrorSource('fetch');
      return;
    }
    if (view === 'pipeline' && pipelines.errorCause != null) {
      setPageError(pageErrorFromUnknown(pipelines.errorCause, "Couldn't load deploy pipeline status"));
      setPageErrorSource('fetch');
      return;
    }
    if (view === 'pipeline' && mergeFreeze.errorCause != null) {
      setPageError(pageErrorFromUnknown(mergeFreeze.errorCause, "Couldn't load merge freeze status"));
      setPageErrorSource('fetch');
      return;
    }
    if (view === 'stats' && stats.errorCause != null) {
      setPageError(pageErrorFromUnknown(stats.errorCause, "Couldn't load deployment stats"));
      setPageErrorSource('fetch');
      return;
    }

    if (pageErrorSource === 'fetch') {
      setPageError(null);
      setPageErrorSource(null);
    }
  }, [
    current.errorCause,
    history.errorCause,
    mergeFreeze.errorCause,
    pageErrorSource,
    pipelines.errorCause,
    stats.errorCause,
    view,
  ]);

  const toggleBundledCommits = (item: DeploymentRecord) => {
    const key = deploymentKey(item);
    setExpandedBundledKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancel = async (item: DeploymentRecord) => {
    const confirmed = window.confirm(
      `Cancel the in-progress ${item.environment} ${item.mode} deploy for ${item.commitShaShort}?`
    );
    if (!confirmed) return;

    setCancellingRunIds((prev) => new Set(prev).add(item.workflowRunId));
    try {
      await cancelDeployment({ workflowRunId: item.workflowRunId });
      setPageError(null);
      setPageErrorSource(null);
      refresh();
    } catch (err) {
      showPageError(err, "Couldn't cancel deploy");
    } finally {
      setCancellingRunIds((prev) => {
        const next = new Set(prev);
        next.delete(item.workflowRunId);
        return next;
      });
    }
  };

  const currentForEnvironment = useMemo(() => {
    return current.data?.items.find((item) => item.environment === environment) ?? null;
  }, [current.data, environment]);

  // Only a runtime-mode pointer reflects the running image; an infrastructure pointer can be
  // newer without changing what is deployed, and would falsely trip the workflow's runtime check.
  const deployedRuntimeSha = useMemo(() => {
    if (rollbackTarget == null) return undefined;
    const item = current.data?.items.find((candidate) => candidate.environment === rollbackTarget.environment);
    return item?.current?.mode === 'runtime' ? item.current.commitSha : undefined;
  }, [current.data, rollbackTarget]);

  const pipelineStatus = (pipeline: DeploymentPipeline) =>
    pipelines.data?.items.find((item) => item.pipeline === pipeline) ?? null;

  const inFlightDeployments = useMemo(() => {
    return [...(history.data?.items ?? [])]
      .filter((item) => item.status === 'in_progress')
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }, [history.data]);

  const terminalHistory = useMemo(() => {
    const items = history.data?.items ?? [];
    return items.filter((item) => item.status !== 'in_progress' && !isBundledIntoLaterSuccess(item, items));
  }, [history.data]);

  const maxHistoryDurationSeconds = useMemo(() => {
    let max = 0;
    for (const item of terminalHistory) {
      const duration = totalDurationSeconds(item);
      if (duration != null && duration > max) max = duration;
    }
    return max;
  }, [terminalHistory]);

  const selectedDeployment = useMemo(() => {
    if (selectedRunId === '') return null;
    const fromHistory = (history.data?.items ?? []).find((item) => item.workflowRunId === selectedRunId);
    if (fromHistory != null) return fromHistory;
    const currentItem = current.data?.items.find((item) => item.current?.workflowRunId === selectedRunId)?.current;
    return currentItem ?? null;
  }, [current.data, history.data, selectedRunId]);

  return (
    <div className='min-w-0 max-w-full space-y-8 pt-2'>
      <h2 className='text-xl font-medium text-neutral-900'>Deployments</h2>

      <DeploymentsTabRow
        view={view}
        environment={environment}
        onSelectPipeline={(nextEnvironment) => {
          setEnvironment(nextEnvironment);
          setView('pipeline');
        }}
        onSelectStats={() => setView('stats')}
      />

      {pageError != null && (
        <div
          role='alert'
          className='flex min-w-0 items-start justify-between gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900'
        >
          <div className='min-w-0'>
            <p className='font-medium'>{pageError.title}</p>
            <p className='mt-0.5 break-words'>{pageError.message}</p>
            {pageError.status != null && <p className='mt-1 text-xs text-red-700/80'>HTTP {pageError.status}</p>}
          </div>
          <button
            type='button'
            className='shrink-0 rounded-md px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-100'
            onClick={() => {
              setPageError(null);
              setPageErrorSource(null);
            }}
            aria-label='Dismiss error'
          >
            Dismiss
          </button>
        </div>
      )}

      {actionWarning != null && (
        <div
          role='status'
          className='flex min-w-0 items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950'
        >
          <div className='min-w-0'>
            <p className='font-medium'>Action succeeded with a warning</p>
            <p className='mt-0.5 break-words'>{actionWarning}</p>
          </div>
          <button
            type='button'
            className='shrink-0 rounded-md px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100'
            onClick={() => setActionWarning(null)}
            aria-label='Dismiss warning'
          >
            Dismiss
          </button>
        </div>
      )}

      {view === 'stats' ? (
        <StatsView
          stats={stats}
          statsEnvironment={statsEnvironment}
          mode={mode}
          days={days}
          onStatsEnvironmentChange={setStatsEnvironment}
          onModeChange={setMode}
          onDaysChange={setDays}
        />
      ) : (
        <>
          <DeployControlsCard
            statuses={pipelines.data?.items ?? []}
            isLoading={pipelines.isLoading}
            canMutate={canMutate}
            onToggleAll={(action) => setPipelinesDialog({ pipeline: 'all', action })}
            mergeControl={
              canMutate && (
                <button
                  type='button'
                  className={
                    mergeFreeze.data?.enabled ? ACTION_BUTTON_PRIMARY_CLASS : ACTION_BUTTON_DANGER_OUTLINE_CLASS
                  }
                  disabled={mergeFreeze.isLoading || mergeFreeze.data == null || mergeFreeze.errorCause != null}
                  onClick={() => {
                    if (mergeFreeze.data != null) setMergeFreezeDialog({ enabled: mergeFreeze.data.enabled });
                  }}
                >
                  {mergeFreeze.isLoading
                    ? 'Loading merge state…'
                    : mergeFreeze.errorCause != null || mergeFreeze.data == null
                    ? 'Merge status unavailable'
                    : mergeFreeze.data.enabled
                    ? 'Resume merges'
                    : 'Pause merges'}
                </button>
              )
            }
          >
            <PipelineControls
              pipeline={environment}
              status={pipelineStatus(environment)}
              isLoading={pipelines.isLoading}
              canMutate={canMutate}
              onToggle={(action) => setPipelinesDialog({ pipeline: environment, action })}
            />
            <PipelineControls
              pipeline='ota'
              status={pipelineStatus('ota')}
              isLoading={pipelines.isLoading}
              canMutate={canToggleOta(user)}
              onToggle={(action) => setPipelinesDialog({ pipeline: 'ota', action })}
            />
          </DeployControlsCard>

          {history.isLoading ? (
            <section className='min-w-0'>
              <h3 className={`mb-3 ${SECTION_HEADING_CLASS}`}>In flight</h3>
              <div className={IN_FLIGHT_CARD_CLASS}>
                <div className='flex gap-3'>
                  <Skeleton className='mt-1 size-2.5 rounded-full' />
                  <div className='min-w-0 flex-1 space-y-2'>
                    <Skeleton className='h-4 w-28' />
                    <Skeleton className='h-3 w-44' />
                    <Skeleton className='mt-3 h-2 w-full' />
                  </div>
                </div>
              </div>
            </section>
          ) : (
            inFlightDeployments.length > 0 && (
              <section className='min-w-0'>
                <h3 className={`mb-3 ${SECTION_HEADING_CLASS}`}>In flight</h3>
                <div className='grid min-w-0 grid-cols-1 gap-3'>
                  {inFlightDeployments.map((item) => (
                    <div
                      key={deploymentKey(item)}
                      className={`${IN_FLIGHT_CARD_CLASS} ${
                        selectedRunId === item.workflowRunId ? 'ring-1 ring-blue-300' : 'cursor-pointer'
                      }`}
                      onClick={() => setSelectedRunId(item.workflowRunId)}
                    >
                      <div className='mb-2 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
                        <div className='flex min-w-0 items-start gap-2.5'>
                          <StatusDot status={item.status} />
                          <div className='min-w-0'>
                            {item.pullRequests.length > 0 ? (
                              <DeploymentPullRequests
                                pullRequests={item.pullRequests}
                                compact
                                deploymentCommitSha={item.commitSha}
                              />
                            ) : (
                              <div className='min-w-0 break-all text-sm font-medium'>
                                <CommitLink sha={item.commitSha} short={item.commitShaShort} />
                              </div>
                            )}
                            <p className='mt-0.5 text-xs text-neutral-500'>
                              {item.mode} · started {relativeTime(item.startedAt)}
                              {item.phase != null ? ` · ${item.phase}` : ''}
                            </p>
                          </div>
                        </div>
                        <DeploymentActions
                          item={item}
                          cancelling={cancellingRunIds.has(item.workflowRunId)}
                          canMutate={canMutate}
                          onCancel={() => void handleCancel(item)}
                          onRollback={() =>
                            setRollbackTarget({ environment: item.environment, commitSha: item.commitSha })
                          }
                        />
                      </div>
                      <DeploymentTimeline record={item} className='mt-3' />
                    </div>
                  ))}
                </div>
              </section>
            )
          )}

          <section className='min-w-0'>
            <div className='mb-3 flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'>
              <h3 className={SECTION_HEADING_CLASS}>Currently deployed</h3>
            </div>
            {current.isLoading ? (
              <div className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white px-4 py-3.5'>
                <Skeleton className='h-3 w-20' />
                <Skeleton className='mt-2 h-5 w-32' />
                <Skeleton className='mt-2 h-3 w-48' />
              </div>
            ) : (
              <div
                className={`min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white px-4 py-3.5 ${
                  currentForEnvironment?.current != null ? 'cursor-pointer' : ''
                }`}
                onClick={() => {
                  if (currentForEnvironment?.current != null) {
                    setSelectedRunId(currentForEnvironment.current.workflowRunId);
                  }
                }}
              >
                <div className='flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                  <div className='min-w-0'>
                    {currentForEnvironment?.current == null ? (
                      <>
                        <p className='text-xs font-medium uppercase tracking-wide text-neutral-500'>{environment}</p>
                        <p className='mt-1 text-sm text-neutral-500'>No successful deploy recorded yet</p>
                      </>
                    ) : (
                      <>
                        <div className='flex min-w-0 items-baseline gap-2'>
                          <p className='shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-500'>
                            {environment}
                          </p>
                          {currentForEnvironment.current.pullRequests.length > 0 ? (
                            <DeploymentPullRequests
                              pullRequests={currentForEnvironment.current.pullRequests}
                              compact
                              deploymentCommitSha={currentForEnvironment.current.commitSha}
                            />
                          ) : (
                            <div className='min-w-0 break-all text-base font-medium'>
                              <CommitLink
                                sha={currentForEnvironment.current.commitSha}
                                short={currentForEnvironment.current.commitShaShort}
                              />
                            </div>
                          )}
                        </div>
                        <p className='mt-1 text-xs text-neutral-500'>
                          {[
                            currentForEnvironment.current.mode,
                            currentForEnvironment.current.buildDurationSeconds != null
                              ? `build ${formatDuration(currentForEnvironment.current.buildDurationSeconds)}`
                              : null,
                            currentForEnvironment.current.deployDurationSeconds != null
                              ? `deploy ${formatDuration(currentForEnvironment.current.deployDurationSeconds)}`
                              : null,
                            relativeTime(currentForEnvironment.current.completedAt),
                          ]
                            .filter((part): part is string => part != null)
                            .join(' · ')}
                        </p>
                      </>
                    )}
                  </div>
                  {currentForEnvironment?.current != null && (
                    <div className={ACTION_CLUSTER_CLASS} onClick={(event) => event.stopPropagation()}>
                      <a
                        href={currentForEnvironment.current.workflowRunUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={ACTION_BUTTON_SECONDARY_CLASS}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(currentForEnvironment.current!.workflowRunUrl, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        Workflow
                        <ExternalLinkIcon />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          <section className='min-w-0'>
            <div className='mb-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
              <h3 className={SECTION_HEADING_CLASS}>History</h3>
              <div className='flex min-w-0 items-center gap-2'>
                <label
                  htmlFor='deployment-history-mode'
                  className='text-xs font-medium uppercase tracking-wide text-neutral-500'
                >
                  Mode
                </label>
                <select
                  id='deployment-history-mode'
                  className={MODE_SELECT_CLASS}
                  value={mode}
                  onChange={(event) => setMode(event.target.value as DeploymentMode | 'all')}
                >
                  <option value='all'>All modes</option>
                  {DEPLOYMENT_MODES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {history.isLoading ? (
              <ul className='min-w-0 divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 bg-white'>
                {[0, 1, 2, 3].map((index) => (
                  <li key={index} className='px-4 py-3.5'>
                    <div className='flex gap-3'>
                      <Skeleton className='mt-1.5 size-2.5 rounded-full' />
                      <div className='min-w-0 flex-1 space-y-2'>
                        <Skeleton className='h-4 w-24' />
                        <Skeleton className='h-3 w-36' />
                        <Skeleton className='h-8 w-full max-w-xs sm:ml-auto sm:w-36' />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : history.data != null && terminalHistory.length === 0 ? (
              <div className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white px-4 py-5'>
                <p className='text-sm text-neutral-500'>
                  No deployment records yet. History fills in as deploy workflows run.
                </p>
              </div>
            ) : (
              terminalHistory.length > 0 && (
                <ul className='min-w-0 divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200'>
                  {terminalHistory.map((item) => {
                    const key = deploymentKey(item);
                    return (
                      <HistoryRow
                        key={key}
                        item={item}
                        maxDurationSeconds={maxHistoryDurationSeconds}
                        historyItems={history.data?.items ?? []}
                        expandedBundled={expandedBundledKeys.has(key)}
                        cancelling={cancellingRunIds.has(item.workflowRunId)}
                        canMutate={canMutate}
                        selected={selectedRunId === item.workflowRunId}
                        onToggleBundled={() => toggleBundledCommits(item)}
                        onCancel={() => void handleCancel(item)}
                        onRollback={() =>
                          setRollbackTarget({ environment: item.environment, commitSha: item.commitSha })
                        }
                        onSelect={() => setSelectedRunId(item.workflowRunId)}
                      />
                    );
                  })}
                </ul>
              )
            )}
          </section>
        </>
      )}

      {selectedDeployment != null && (
        <DeploymentDrawer
          item={selectedDeployment}
          canMutate={canMutate}
          onClose={() => setSelectedRunId('')}
          onRollback={() =>
            setRollbackTarget({
              environment: selectedDeployment.environment,
              commitSha: selectedDeployment.commitSha,
            })
          }
        />
      )}

      {rollbackTarget != null && (
        <RollbackDialog
          environment={rollbackTarget.environment}
          initialCommitSha={rollbackTarget.commitSha}
          deployedCommitSha={deployedRuntimeSha}
          onClose={() => setRollbackTarget(null)}
          onSuccess={() => {
            setPageError(null);
            setPageErrorSource(null);
            refresh();
          }}
          onError={(err) => showPageError(err, "Couldn't dispatch rollback")}
        />
      )}

      {mergeFreezeDialog != null && (
        <MergeFreezeDialog
          enabled={mergeFreezeDialog.enabled}
          onClose={() => setMergeFreezeDialog(null)}
          onSuccess={() => {
            setPageError(null);
            setPageErrorSource(null);
            void mergeFreeze.refetch();
          }}
          onError={(err) => showPageError(err, "Couldn't dispatch merge freeze")}
        />
      )}

      {pipelinesDialog != null && (
        <DisablePipelinesDialog
          pipeline={pipelinesDialog.pipeline}
          action={pipelinesDialog.action}
          onClose={() => setPipelinesDialog(null)}
          onSuccess={(result) => {
            setPageError(null);
            setPageErrorSource(null);
            setActionWarning(result.slackWarning ?? null);
            refresh();
          }}
          onError={(err) =>
            showPageError(
              err,
              pipelinesDialog.action === 'disable'
                ? "Couldn't disable deploy pipelines"
                : "Couldn't enable deploy pipelines"
            )
          }
        />
      )}
    </div>
  );
};

/** Workflows GitHub reports as disabled; `unknown` (unreadable) states are not counted. */
function disabledWorkflowFiles(status: DeploymentPipelineStatus | null | undefined): string[] {
  return (status?.workflows ?? [])
    .filter((workflow) => workflow.state !== 'active' && workflow.state !== 'unknown')
    .map((workflow) => workflow.workflowFile);
}

function DeployControlsCard({
  statuses,
  isLoading,
  canMutate,
  onToggleAll,
  mergeControl,
  children,
}: {
  statuses: DeploymentPipelineStatus[];
  isLoading: boolean;
  canMutate: boolean;
  onToggleAll: (action: DeploymentPipelineAction) => void;
  mergeControl: ReactNode;
  children: ReactNode;
}) {
  const tracked = DEPLOYMENT_PIPELINES.map((pipeline) => statuses.find((item) => item.pipeline === pipeline));
  const statusAvailable = tracked.every((item) => item?.statusAvailable === true);
  const anyDisabled = tracked.some((item) => item?.disabled === true || disabledWorkflowFiles(item).length > 0);
  // Partially disabled still offers "Disable all", so one click finishes stopping everything.
  const allDisabled = statusAvailable && tracked.every((item) => item?.disabled === true);

  // Calm by default; the card only draws attention once something is actually disabled.
  return (
    <section
      className={`min-w-0 overflow-hidden rounded-lg border bg-white ${
        anyDisabled ? 'border-amber-300' : 'border-neutral-200'
      }`}
    >
      <div
        className={`flex min-w-0 flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
          allDisabled ? 'border-red-200 bg-red-50' : 'border-neutral-200'
        }`}
      >
        <div className='min-w-0'>
          <h3 className={SECTION_HEADING_CLASS}>Deploy controls</h3>
          <p className={`mt-0.5 text-sm ${allDisabled ? 'font-medium text-red-900' : 'text-neutral-500'}`}>
            {allDisabled
              ? 'Staging, production, and OTA deploys are all disabled.'
              : 'Turn deploy workflows on or off in GitHub Actions. Rollback stays available.'}
          </p>
        </div>
        {(canMutate || mergeControl) && (
          <div className={ACTION_CLUSTER_CLASS}>
            {mergeControl}
            {canMutate &&
              (isLoading ? (
                <Skeleton className='h-8 w-36' />
              ) : (
                <button
                  type='button'
                  className={allDisabled ? ACTION_BUTTON_PRIMARY_CLASS : ACTION_BUTTON_DANGER_OUTLINE_CLASS}
                  disabled={!statusAvailable}
                  title={statusAvailable ? undefined : 'Cannot change deploys until GitHub workflow status is readable'}
                  onClick={() => onToggleAll(allDisabled ? 'enable' : 'disable')}
                >
                  {allDisabled ? 'Enable all deploys' : 'Disable all deploys'}
                </button>
              ))}
          </div>
        )}
      </div>
      <div className='divide-y divide-neutral-200'>{children}</div>
    </section>
  );
}

function PipelineControls({
  pipeline,
  status,
  isLoading,
  canMutate,
  onToggle,
}: {
  pipeline: DeploymentPipeline;
  status: DeploymentPipelineStatus | null;
  isLoading: boolean;
  canMutate: boolean;
  onToggle: (action: DeploymentPipelineAction) => void;
}) {
  const isOta = pipeline === 'ota';
  const disabled = status?.disabled === true;
  const statusAvailable = status?.statusAvailable === true;
  const warning = status?.warning ?? null;
  // A pipeline with several workflows (OTAs) can be half disabled, e.g. after a manual change in GitHub.
  const partlyDisabledFiles = statusAvailable && !disabled ? disabledWorkflowFiles(status) : [];
  const partlyDisabled = partlyDisabledFiles.length > 0;
  const actions: DeploymentPipelineAction[] = disabled
    ? ['enable']
    : partlyDisabled
    ? ['enable', 'disable']
    : ['disable'];
  const pipelineTitle = `${pipeline.charAt(0).toUpperCase()}${pipeline.slice(1)}`;
  // The OTA row renders on every environment tab, so its copy always says it is global.
  const subject = isOta ? 'OTA updates for all environments' : `${pipeline} deploys`;

  let statusText: string;
  if (!statusAvailable) {
    statusText = isOta ? 'OTA update workflow status unavailable' : `${pipelineTitle} workflow status unavailable`;
  } else if (partlyDisabled) {
    statusText = `${isOta ? 'OTA update' : pipelineTitle + ' deploy'} workflows are partly disabled`;
  } else if (isOta) {
    statusText = `OTA update workflows are ${disabled ? 'disabled' : 'enabled'} for all environments`;
  } else {
    statusText = `${pipelineTitle} deploy workflows are ${disabled ? 'disabled' : 'enabled'}`;
  }

  return (
    <div className='min-w-0 space-y-3 px-4 py-3'>
      <div className='flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='min-w-0'>
          <h4 className='text-sm font-semibold text-neutral-800'>
            {isOta ? 'OTA updates' : `${pipelineTitle} deploys`}
          </h4>
          {isLoading ? (
            <div className='mt-2 space-y-2'>
              <Skeleton className='h-4 w-56' />
              <Skeleton className='h-3 w-40' />
            </div>
          ) : (
            <p className='mt-0.5 text-sm text-neutral-500'>{statusText}</p>
          )}
        </div>
        {canMutate && (
          <div className={ACTION_CLUSTER_CLASS}>
            {isLoading ? (
              <Skeleton className='h-8 w-36' />
            ) : (
              actions.map((action) => (
                <button
                  key={action}
                  type='button'
                  className={action === 'enable' ? ACTION_BUTTON_PRIMARY_CLASS : ACTION_BUTTON_SECONDARY_CLASS}
                  disabled={!statusAvailable}
                  title={
                    statusAvailable
                      ? `${action === 'enable' ? 'Enable' : 'Disable'} ${subject}`
                      : `Cannot change ${subject} until GitHub workflow status is readable`
                  }
                  onClick={() => onToggle(action)}
                >
                  {action === 'enable' ? 'Enable' : 'Disable'} {isOta ? 'OTAs' : 'deploys'}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {!isLoading && warning != null && (
        <div role='status' className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950'>
          <p className='font-medium'>
            {isOta ? 'OTA update workflow status unavailable' : `Deploy workflow status unavailable for ${pipeline}`}
          </p>
          <p className='mt-1 text-amber-900/80'>{warning}</p>
        </div>
      )}

      {!isLoading && partlyDisabled && (
        <div role='status' className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950'>
          <p className='font-medium'>Some {isOta ? 'OTA update' : `${pipeline} deploy`} workflows are disabled</p>
          <p className='mt-1 text-amber-900/80'>
            Disabled in GitHub Actions: {partlyDisabledFiles.join(', ')}. Enable or disable to put them all in the same
            state.
          </p>
        </div>
      )}

      {!isLoading && disabled && (
        <div role='status' className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950'>
          {isOta ? (
            <>
              <p className='font-medium'>OTA updates disabled for all environments</p>
              <p className='mt-1 text-amber-900/80'>
                OTA update workflows are disabled in GitHub Actions, so no staging or production OTA can be published.
                EAS builds are unaffected.
              </p>
            </>
          ) : (
            <>
              <p className='font-medium'>Deploy workflows disabled for {pipeline}</p>
              <p className='mt-1 text-amber-900/80'>
                Runtime and infrastructure deploy workflows are disabled in GitHub Actions (not{' '}
                <code className='rounded bg-amber-100/80 px-1'>FREEZE_*</code> variables). Rollback remains available.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatsView({
  stats,
  statsEnvironment,
  mode,
  days,
  onStatsEnvironmentChange,
  onModeChange,
  onDaysChange,
}: {
  stats: ReturnType<typeof useFetch<Awaited<ReturnType<typeof getDeploymentStats>>>>;
  statsEnvironment: DeploymentEnvironment | 'all';
  mode: DeploymentMode | 'all';
  days: DeploymentStatsDays;
  onStatsEnvironmentChange: (value: DeploymentEnvironment | 'all') => void;
  onModeChange: (value: DeploymentMode | 'all') => void;
  onDaysChange: (value: DeploymentStatsDays) => void;
}) {
  return (
    <section className='min-w-0 space-y-6'>
      <div className='flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'>
        <h3 className={SECTION_HEADING_CLASS}>Stats</h3>
        <div className='flex min-w-0 flex-wrap gap-2'>
          <select
            className={MODE_SELECT_CLASS}
            value={statsEnvironment}
            onChange={(event) => onStatsEnvironmentChange(event.target.value as DeploymentEnvironment | 'all')}
          >
            <option value='all'>All environments</option>
            {DEPLOYMENT_ENVIRONMENTS.map((value) => (
              <option key={value} value={value}>
                {value === 'staging' ? 'Staging' : 'Production'}
              </option>
            ))}
          </select>
          <select
            className={MODE_SELECT_CLASS}
            value={mode}
            onChange={(event) => onModeChange(event.target.value as DeploymentMode | 'all')}
          >
            <option value='all'>All modes</option>
            {DEPLOYMENT_MODES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <div className='flex flex-wrap gap-1'>
            {DEPLOYMENT_STATS_DAY_OPTIONS.map((value) => (
              <button
                key={value}
                type='button'
                onClick={() => onDaysChange(value)}
                className={`rounded px-3 py-1 text-sm transition-colors ${
                  days === value
                    ? 'bg-neutral-900 text-white'
                    : 'border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
                }`}
              >
                {value}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {stats.isLoading ? (
        <>
          <div className='grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6'>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div key={index} className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white p-4'>
                <Skeleton className='h-3 w-16' />
                <Skeleton className='mt-2 h-8 w-20' />
              </div>
            ))}
          </div>
          <div className='min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white p-4'>
            <Skeleton className='h-4 w-40' />
            <Skeleton className='mt-2 h-3 w-64' />
            <Skeleton className='mt-6 h-72 w-full' />
          </div>
        </>
      ) : (
        stats.data != null && (
          <>
            <div className='grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6'>
              <StatCard label='Deploys' value={String(stats.data.total)} />
              <StatCard
                label='Success rate'
                value={stats.data.successRate != null ? `${stats.data.successRate}%` : '—'}
              />
              <StatCard label='P50 duration' value={formatDuration(stats.data.p50DurationSeconds)} />
              <StatCard label='P90 duration' value={formatDuration(stats.data.p90DurationSeconds)} />
              <StatCard label='Cancelled' value={String(stats.data.cancelled)} />
              <StatCard label='Skipped' value={String(stats.data.skipped)} />
            </div>
            <ThroughputChart stats={stats.data} />
          </>
        )
      )}
    </section>
  );
}

function DeploymentActions({
  item,
  cancelling,
  canMutate,
  onCancel,
  onRollback,
}: {
  item: DeploymentRecord;
  cancelling: boolean;
  canMutate: boolean;
  onCancel: () => void;
  onRollback: () => void;
}) {
  return (
    <div className={ACTION_CLUSTER_CLASS}>
      <a
        href={item.workflowRunUrl}
        target='_blank'
        rel='noopener noreferrer'
        className={ACTION_BUTTON_SECONDARY_CLASS}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          window.open(item.workflowRunUrl, '_blank', 'noopener,noreferrer');
        }}
      >
        Workflow
        <ExternalLinkIcon />
      </a>
      {canMutate && item.status === 'in_progress' && (
        <button
          type='button'
          className={ACTION_BUTTON_DANGER_OUTLINE_CLASS}
          disabled={cancelling}
          onClick={(event) => {
            event.stopPropagation();
            onCancel();
          }}
        >
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </button>
      )}
      {canMutate && canRollbackDeployment(item) && (
        <button
          type='button'
          className={ACTION_BUTTON_DANGER_OUTLINE_CLASS}
          onClick={(event) => {
            event.stopPropagation();
            onRollback();
          }}
        >
          Rollback to here
        </button>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className='w-full min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white p-4'>
      <p className='text-xs uppercase tracking-wide text-neutral-500'>{label}</p>
      <p className='mt-1 break-words text-2xl font-medium text-neutral-900'>{value}</p>
    </div>
  );
}

function ExternalLinkIcon() {
  return (
    <svg aria-hidden='true' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.5' className='size-3'>
      <path d='M6.5 3.5H3.5A1.5 1.5 0 0 0 2 5v7.5A1.5 1.5 0 0 0 3.5 14H11a1.5 1.5 0 0 0 1.5-1.5V9.5' />
      <path d='M9 2h5v5' />
      <path d='M14 2 7.5 8.5' />
    </svg>
  );
}
